// Shared timestamp formatter for staff-facing UI.
//
// The API emits SQLite's bare "YYYY-MM-DD HH:MM:SS" form (UTC, no zone
// marker). Rendering it ad-hoc has bitten us two ways:
//   1. `new Date("2026-06-10 09:00:00")` is "Invalid Date" in Safari —
//      the space form is not ISO-8601; only "T"/"Z" forms parse there.
//   2. Where it does parse, a zoneless stamp is read as LOCAL time,
//      silently shifting the displayed hour by the viewer's offset; and
//      some views leaked the raw UTC string straight onto the screen or
//      into a `title` tooltip.
//
// This module centralises the parse + localise so every staff view is
// consistent: parse as UTC, render via en-GB with a short zone label so
// the viewer always knows which clock they're reading.

const EM_DASH = "—";

// Parse a DB timestamp into a Date, treating a zoneless stamp as UTC.
// Accepts the bare-space "YYYY-MM-DD HH:MM:SS" form (replacing the space
// with "T" so Safari parses it) and the ISO "T" form, appending "Z" only
// when the stamp carries no zone (a stamp that already ends in "Z" or a
// "+HH:MM"/"-HH:MM" offset is left untouched — a second "Z" would yield
// "Invalid Date"). Returns null for empty/whitespace/unparseable input.
export function parseDbStamp(
  stamp: string | null | undefined,
): Date | null {
  if (!stamp) return null;
  const trimmed = stamp.trim();
  if (!trimmed) return null;
  // Normalise the bare-space form to ISO "T" so Safari can parse it.
  const isoish = trimmed.includes(" ")
    ? trimmed.replace(" ", "T")
    : trimmed;
  const hasZone = /[Zz]$/.test(isoish) || /[+-]\d{2}:?\d{2}$/.test(isoish);
  const d = new Date(hasZone ? isoish : isoish + "Z");
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

// Short zone label for the viewer's locale (e.g. "UTC", "GMT+1", "EDT",
// "GMT+5:30"). Used both as a per-stamp suffix and for a one-line
// "times shown in <zone>" note. Falls back to "local time" if the
// runtime can't produce a zone name.
export function timeZoneLabel(at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZoneName: "short",
    }).formatToParts(at);
    const zone = parts.find((p) => p.type === "timeZoneName")?.value;
    return zone ?? "local time";
  } catch {
    return "local time";
  }
}

// "10 Jun 2026, 09:00 GMT+1" — localised date+time with a short zone
// label so a UTC-stored stamp is never ambiguous. Returns "—" for
// empty/invalid input. Pass `withZone: false` to omit the zone suffix
// (e.g. when the surrounding UI already states the zone once).
export function formatDateTime(
  stamp: string | null | undefined,
  opts: { withZone?: boolean } = {},
): string {
  const d = parseDbStamp(stamp);
  if (!d) return EM_DASH;
  const { withZone = true } = opts;
  const base = d.toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  });
  return withZone ? `${base} ${timeZoneLabel(d)}` : base;
}

// "10 Jun 2026" — localised date only (no time, no zone suffix; a
// date-only value has no meaningful clock to label). Returns "—" for
// empty/invalid input.
export function formatDate(stamp: string | null | undefined): string {
  const d = parseDbStamp(stamp);
  if (!d) return EM_DASH;
  return d.toLocaleDateString("en-GB", {
    dateStyle: "medium",
  });
}
